import React, { Fragment } from 'react'
import styled from '@emotion/styled'
import Img from 'gatsby-image'

import theme from '../styles/theme'
import { media, rem } from '../styles/tools'

import { useNavbarTheme } from '../contexts/NavbarTheme'

import useInView from '../hooks/useInView'

import Wrapper from '../components/Wrapper'
import SEO from '../components/SEO'
import Shell from '../components/Shell'
import Space from '../components/Space'
import Link from '../components/Link'
import { ContentAboveFooter } from '../components/Footer'
import ChallengeUsFooter from '../components/ChallengeUsFooter'
import HelloFab from '../components/HelloFab'
import { Headline } from '../styles/typography'

import { getTeamMemberDataFromSlug } from '../team'

const ModalCloseButton = styled.div({
  display: 'block',
  width: rem(60),
  height: rem(60),
  position: 'relative',
  color: '#bbb',
  border: 'none',
  outline: 'none',
  background: '#000',
  cursor: 'pointer',
  transition: '0.2s',

  '&:hover::before, &:hover::after': {
    transform: 'none',
  },

  '&::before, &::after': {
    content: '""',
    display: 'block',
    width: '56%',
    height: 2,
    position: 'absolute',
    top: '50%',
    left: '22%',
    transform: 'rotate(45deg)',
    transition: '0.3s',
    backgroundColor: 'currentColor',
  },

  '&::after': {
    transform: 'rotate(-45deg)',
  },
})

const icons = {
  linkedin: props => (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="#006699" {...props}>
      <path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z" />
    </svg>
  ),
  twitter: props => (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="#5aaeeb" {...props}>
      <path d="M22.46,6C21.69,6.35 20.86,6.58 20,6.69C20.88,6.16 21.56,5.32 21.88,4.31C21.05,4.81 20.13,5.16 19.16,5.36C18.37,4.5 17.26,4 16,4C13.65,4 11.73,5.92 11.73,8.29C11.73,8.63 11.77,8.96 11.84,9.27C8.28,9.09 5.11,7.38 3,4.79C2.63,5.42 2.42,6.16 2.42,6.94C2.42,8.43 3.17,9.75 4.33,10.5C3.62,10.5 2.96,10.3 2.38,10C2.38,10 2.38,10 2.38,10.03C2.38,12.11 3.86,13.85 5.82,14.24C5.46,14.34 5.08,14.39 4.69,14.39C4.42,14.39 4.15,14.36 3.89,14.31C4.43,16 6,17.26 7.89,17.29C6.43,18.45 4.58,19.13 2.56,19.13C2.22,19.13 1.88,19.11 1.54,19.07C3.44,20.29 5.7,21 8.12,21C16,21 20.33,14.46 20.33,8.79C20.33,8.6 20.33,8.42 20.32,8.23C21.16,7.63 21.88,6.87 22.46,6Z" />
    </svg>
  ),
  codepen: props => (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="#111" {...props}>
      <path d="M15.09,12L12,14.08V14.09L8.91,12L12,9.92V9.92L15.09,12M12,2C11.84,2 11.68,2.06 11.53,2.15L2.5,8.11C2.27,8.22 2.09,8.43 2,8.67V14.92C2,15.33 2,15.33 2.15,15.53L11.53,21.86C11.67,21.96 11.84,22 12,22C12.16,22 12.33,21.95 12.47,21.85L21.85,15.5C22,15.33 22,15.33 22,14.92V8.67C21.91,8.42 21.73,8.22 21.5,8.1L12.47,2.15C12.32,2.05 12.16,2 12,2M16.58,13L19.59,15.04L12.83,19.6V15.53L16.58,13M19.69,8.9L16.58,11L12.83,8.47V4.38L19.69,8.9M20.33,10.47V13.53L18.07,12L20.33,10.47M7.42,13L11.17,15.54V19.6L4.41,15.04L7.42,13M4.31,8.9L11.17,4.39V8.5L7.42,11L4.31,8.9M3.67,10.5L5.93,12L3.67,13.54V10.5Z" />
    </svg>
  ),
  instagram: props => (
    <svg viewBox="0 0 24 24" width={19} height={19} {...props}>
      <radialGradient
        id="a"
        cx={7.452}
        cy={3.212}
        r={21.86}
        gradientTransform="matrix(1 0 0 -1 0 26)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset={0} stopColor="#ffd776" />
        <stop offset={0.246} stopColor="#f3a554" />
        <stop offset={0.379} stopColor="#f15c3c" />
        <stop offset={0.539} stopColor="#d94867" />
        <stop offset={0.7} stopColor="#c32f87" />
        <stop offset={0.912} stopColor="#7d63a7" />
        <stop offset={0.985} stopColor="#5c6cb3" />
      </radialGradient>
      <path
        fill="url(#a)"
        d="M22.3 4.9C22 4.2 21.6 3.6 21 3c-.6-.6-1.2-1-1.9-1.2-.7-.3-1.5-.5-2.6-.5-1.2-.1-1.5-.1-4.5-.1s-3.3 0-4.5.1c-1.1 0-1.9.2-2.6.4C4.2 2 3.6 2.4 3 3S2 4.2 1.7 4.9c-.2.7-.4 1.5-.5 2.6V12c0 2.9 0 3.3.1 4.5.1 1.2.2 1.9.5 2.6.2.7.6 1.3 1.2 1.9.6.6 1.2 1 1.9 1.2.7.3 1.5.5 2.6.5 1.2.1 1.5.1 4.5.1s3.3 0 4.5-.1c1.2-.1 1.9-.2 2.6-.5.7-.3 1.3-.6 1.9-1.2.6-.6 1-1.2 1.2-1.9.3-.7.5-1.5.5-2.6.1-1.2.1-1.5.1-4.5s0-3.3-.1-4.5c0-1.1-.2-1.9-.4-2.6zm-1.5 11.5c-.1 1.1-.2 1.6-.4 2-.2.5-.4.9-.8 1.2-.4.4-.7.6-1.2.8-.4.2-1 .3-2 .4-1.1.1-1.5.1-4.4.1s-3.2 0-4.4-.1c-1.1-.1-1.6-.2-2-.4-.5-.2-.9-.4-1.2-.8-.4-.4-.6-.7-.8-1.2-.2-.4-.3-1-.4-2-.1-1.1-.1-1.5-.1-4.4s0-3.2.1-4.4c.1-1.1.2-1.6.4-2 .1-.5.4-.9.8-1.2s.7-.6 1.2-.8c.4-.2 1-.3 2-.4 1.2-.1 1.5-.1 4.4-.1s3.2 0 4.4.1c1.1.1 1.6.2 2 .4.5.2.9.4 1.2.8s.6.7.8 1.2c.2.4.3 1 .4 2 .1 1.1.1 1.5.1 4.4s0 3.2-.1 4.4z"
      />
      <radialGradient
        id="b"
        cx={9.66}
        cy={8.46}
        r={11.225}
        gradientTransform="matrix(1 0 0 -1 0 26)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset={0} stopColor="#ffd776" />
        <stop offset={0.246} stopColor="#f3a554" />
        <stop offset={0.379} stopColor="#f15c3c" />
        <stop offset={0.607} stopColor="#d94867" />
        <stop offset={0.835} stopColor="#c32f87" />
        <stop offset={0.958} stopColor="#7d63a7" />
        <stop offset={1} stopColor="#5c6cb3" />
      </radialGradient>
      <path
        fill="url(#b)"
        d="M12 6.4c-3.1 0-5.6 2.5-5.6 5.6s2.5 5.6 5.6 5.6 5.6-2.5 5.6-5.6-2.5-5.6-5.6-5.6zm0 9.2c-2 0-3.6-1.6-3.6-3.6S10 8.4 12 8.4s3.6 1.6 3.6 3.6-1.6 3.6-3.6 3.6z"
      />
      <radialGradient
        id="c"
        cx={17.234}
        cy={18.493}
        r={2.623}
        gradientTransform="matrix(1 0 0 -1 0 26)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset={0} stopColor="#5f6db3" />
        <stop offset={1} stopColor="#5c6cb3" />
      </radialGradient>
      <circle fill="url(#c)" cx={17.8} cy={6.2} r={1.3} />
    </svg>
  ),
}

export default ({ slug, portrait, navigate }) => {
  useNavbarTheme('dark-solid')
  const [inViewRef, inView] = useInView()

  const {
    fullName,
    position,
    quote,
    description,
    links,
  } = getTeamMemberDataFromSlug(slug)

  return (
    <Fragment>
      <SEO title={fullName} />
      <Shell>
        <ContentAboveFooter>
          <Wrapper ref={inViewRef}>
            <Space y={theme.navbarHeight} />
            <div
              css={{
                display: 'grid',
                gridTemplate: `
                  "Img   Close" min-content
                  "Title Title" auto
                  "Desc  Desc"  1fr
                  / 1fr 60px
                `,
                [media.tabletMd]: {
                  gridTemplate: `
                    "Img Title Close" auto
                    "Img Desc  Close" 1fr
                    / 1fr 2fr 60px
                  `,
                },
                color: '#000',
                backgroundColor: '#fff',
              }}
            >
              <a
                href="/agency"
                onClick={e => {
                  e.preventDefault()
                  window.history.length > 1
                    ? window.history.back()
                    : navigate('/agency')
                }}
                css={{
                  gridArea: 'Close',
                  alignSelf: 'start',
                  zIndex: 1,
                  position: 'sticky',
                  top: rem(theme.navbarHeight.mobile),
                  [media.laptop]: {
                    top: rem(theme.navbarHeight.laptop),
                  },
                }}
                aria-label="Close"
              >
                <ModalCloseButton />
              </a>
              <div
                css={{
                  maxHeight: `calc(100vh - ${rem(theme.navbarHeight.mobile)})`,
                  gridArea: 'Img / Img / Title / Close',
                  [media.tabletMd]: { gridArea: 'Img' },
                  backgroundColor: '#212121',
                  overflow: 'hidden',
                  [media.laptop]: {
                    height: `calc(100vh - ${rem(theme.navbarHeight.laptop)})`,
                    position: 'sticky',
                    top: rem(theme.navbarHeight.laptop),
                  },
                }}
              >
                <Img
                  css={{
                    height: '100%',
                    width: '120%',
                    [media.laptop]: {
                      objectPosition: 'center top',
                      transform: `translate(${inView ? -10 : 10}%)`,
                      opacity: inView ? 1 : 0,
                      transition: '1s',
                    },
                  }}
                  imgStyle={{
                    objectPosition: 'center top',
                  }}
                  fluid={portrait}
                />
              </div>
              <Headline
                as="h1"
                css={{
                  gridArea: 'Title',
                  zIndex: 1,
                  padding: rem(30),
                  color: '#fff',
                  [media.tabletMd]: {
                    padding: rem(50),
                    paddingBottom: 0,
                    color: '#000',
                  },
                }}
              >
                {fullName}
                <br />
                {position}
              </Headline>
              <div
                css={{
                  gridArea: 'Desc',
                  padding: 30,
                  [media.tabletMd]: { padding: rem(50) },
                  [media.laptop]: { width: '70%' },
                }}
              >
                {quote && (
                  <blockquote
                    css={{
                      position: 'relative',
                      margin: 0,
                      marginBottom: rem(32),
                      fontWeight: 800,
                      fontFamily: 'RidleyGrotesk, sans-serif',
                      fontSize: rem(26),
                      lineHeight: 1.2,
                      '&::before': { content: "'“'" },
                      '&::after': { content: "'”'" },
                    }}
                  >
                    {quote}
                  </blockquote>
                )}
                {description}
                <Space y={32} />
                <ul
                  css={{ display: 'flex', '> * + *': { marginLeft: rem(16) } }}
                >
                  {links &&
                    Object.entries(links).map(([label, url], i) => {
                      const Icon = icons[label]
                      return (
                        <li key={i}>
                          <Link
                            out
                            href={url}
                            css={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              lineHeight: 1,
                              letterSpacing: '0.2em',
                              fontWeight: 'normal',
                              fontFamily: 'TungstenMedium, sans-serif',
                              textTransform: 'uppercase',
                              color: {
                                linkedin: '#006699',
                                twitter: '#5aaeeb',
                                instagram: '#af20af',
                                codepen: '#111',
                              }[label],
                              svg: { marginRight: rem(8) },
                            }}
                          >
                            <Icon />
                            {label}
                          </Link>
                        </li>
                      )
                    })}
                </ul>
              </div>
            </div>
          </Wrapper>
        </ContentAboveFooter>
        <ChallengeUsFooter />
        <HelloFab />
      </Shell>
    </Fragment>
  )
}
